import React from 'react';
import { motion } from 'framer-motion';
import { MonitorSmartphone, KeyRound, MapPin, FileSpreadsheet, Moon, CalendarDays, ShieldCheck, Users, BellRing, Building2, Mail, CheckCircle2, CalendarRange, Timer } from 'lucide-react';

const fadeIn = (i = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.55, delay: i * 0.08 },
});

const small = [
  { icon: CheckCircle2, t: 'Autocierre con doble aceptación', d: 'Si un empleado olvida marcar la salida, el sistema cierra automáticamente su jornada según su horario y el empleado lo valida al día siguiente. Legal y sin roces.' },
  { icon: Timer, t: 'Horas esperadas 100% flexibles', d: 'Configura horas iguales todos los días, distintas por cada día (L-V con horarios variables) o heredadas del centro o del departamento.' },
  { icon: Moon, t: 'Nocturnidad y horas extra', d: 'Detección automática de la franja 22:00-06:00 (art. 36 ET), cálculo de trabajadores nocturnos legales y ranking de horas extra por persona.' },
  { icon: CalendarDays, t: 'Calendario laboral completo', d: 'Importa los festivos oficiales de tu comunidad y gestiona vacaciones, bajas médicas y permisos de cada empleado.' },
  { icon: ShieldCheck, t: 'Validación legal de cambios', d: 'Si corriges un fichaje a mano, el empleado debe aprobarlo. Todo queda auditado, como exige la normativa.' },
  { icon: BellRing, t: 'Avisos de cumplimiento', d: 'Alertas si no se respetan las 12h de descanso entre jornadas, se superan las 9h o el máximo legal de nocturnidad (art. 36.1).' },
  { icon: Users, t: 'Permisos por rol', d: 'Admin, colaborador, gestoría y auditor con matriz de permisos por módulo. Cada uno ve solo lo que debe ver.' },
  { icon: Building2, t: 'Multi-centro de trabajo', d: 'Varios centros con su propio calendario de festivos y su zona GPS de fichaje delimitada en el mapa.' },
];

export const FeaturesBento = () => (
  <section id="funcionalidades" className="py-20 sm:py-28 bg-bone" data-testid="features-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="max-w-2xl mb-14">
        <p className="font-body-landing text-terracotta font-bold text-sm tracking-[0.15em] uppercase mb-4">Funcionalidades</p>
        <h2 className="font-display font-black text-forest-900 text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
          Todo lo que necesitas para el control horario. Nada que te sobre.
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
        {/* Panel de administración */}
        <motion.div {...fadeIn(0)} className="col-span-1 md:col-span-8 md:row-span-2 rounded-3xl bg-forest-900 noise-overlay text-bone p-8 sm:p-10 border border-forest-900/10 overflow-hidden flex flex-col" data-testid="feature-dashboard">
          <div className="mb-8 max-w-lg">
            <MonitorSmartphone className="w-8 h-8 text-terracotta mb-5" />
            <h3 className="font-display font-bold text-2xl sm:text-3xl mb-3">Panel de control en tiempo real</h3>
            <p className="font-body-landing text-bone/70 leading-relaxed">
              Quién está trabajando, quién descansa y cuántas horas lleva cada uno. Corrige fichajes,
              detecta incidencias GPS y gestiona a todo tu equipo desde un único panel claro y rápido.
            </p>
          </div>
          <div className="relative rounded-2xl overflow-hidden border border-bone/10 shadow-2xl mt-auto translate-y-4">
            <img src="/landing/admin_dashboard.png" alt="Dashboard de administración con estado del personal" className="w-full block" loading="lazy" />
          </div>
        </motion.div>

        {/* Kiosko */}
        <motion.div {...fadeIn(1)} className="col-span-1 md:col-span-4 md:row-span-2 rounded-3xl bg-forest-100 p-8 border border-forest-900/10 flex flex-col" data-testid="feature-kiosk">
          <KeyRound className="w-8 h-8 text-forest-700 mb-5" />
          <h3 className="font-display font-bold text-2xl text-forest-900 mb-3">Kiosko en tablet u ordenador</h3>
          <p className="font-body-landing text-forest-700 text-sm leading-relaxed mb-6">
            Una tablet o un ordenador compartido en la entrada. Cada empleado toca su nombre, marca su PIN y ficha en 2 segundos. Sin apps que instalar ni tarjetas que perder.
          </p>
          <div className="relative rounded-2xl overflow-hidden border border-forest-900/10 shadow-xl mt-auto">
            <img src="/landing/kiosk_pin.png" alt="Kiosko de fichaje con teclado PIN" className="w-full block" loading="lazy" />
          </div>
        </motion.div>

        {/* App móvil GPS */}
        <motion.div {...fadeIn(2)} className="col-span-1 md:col-span-6 rounded-3xl bg-white p-8 border border-forest-900/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col sm:flex-row gap-7 items-center" data-testid="feature-app">
          <div className="flex-1">
            <MapPin className="w-8 h-8 text-terracotta mb-5" />
            <h3 className="font-display font-bold text-2xl text-forest-900 mb-3">App móvil con control GPS</h3>
            <p className="font-body-landing text-forest-700 text-sm leading-relaxed">
              Para comerciales, obras o teletrabajo: tus empleados fichan desde su móvil y el sistema comprueba
              si están dentro de la zona del centro. Si fichan fuera, queda marcado como incidencia.
              Y acceden en un segundo con su huella o Face ID, sin teclear nada.
            </p>
          </div>
          <div className="w-36 shrink-0 rounded-[1.4rem] overflow-hidden border-4 border-forest-900 shadow-xl">
            <img src="/landing/app_empleado.png" alt="App del empleado con fichaje y GPS" className="w-full block" loading="lazy" />
          </div>
        </motion.div>

        {/* Informes profesionales — CARD ESTRELLA */}
        <motion.div {...fadeIn(3)} className="col-span-1 md:col-span-8 rounded-3xl bg-white p-8 sm:p-10 border border-forest-900/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col" data-testid="feature-reports">
          <div className="flex items-start gap-3 mb-5">
            <FileSpreadsheet className="w-8 h-8 text-terracotta shrink-0" />
            <span className="inline-flex items-center gap-1.5 rounded-full bg-terracotta/10 border border-terracotta/25 px-3 py-1 text-xs font-bold text-terracotta">NUEVO</span>
          </div>
          <h3 className="font-display font-bold text-2xl sm:text-3xl text-forest-900 mb-3">Suite de informes profesionales</h3>
          <p className="font-body-landing text-forest-700 text-sm md:text-base leading-relaxed mb-5">
            Seis tipos de informe listos para dirección, RRHH, gestoría o Inspección de Trabajo:
            <b> resumen ejecutivo con KPIs</b>, análisis de horas extra, absentismo, nocturnidad (art. 36 ET),
            cumplimiento legal y productividad por departamento. Todo en PDF con tu marca y Excel multi-hoja.
          </p>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mb-6 font-body-landing text-sm text-forest-700">
            {['Detección automática de trabajadores nocturnos', 'KPIs para el CEO en una hoja', 'Alertas de descansos y jornada máxima', 'Ranking de horas extra por empleado'].map((t) => (
              <li key={t} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-terracotta" />{t}</li>
            ))}
          </ul>
          <div className="rounded-2xl overflow-hidden border border-forest-900/10 shadow-lg mt-auto">
            <img src="/landing/admin_reportes.png" alt="Suite de informes profesionales con KPIs, rankings y export PDF/Excel" className="w-full block" loading="lazy" />
          </div>
        </motion.div>

        {/* Envío automático a la gestoría */}
        <motion.div {...fadeIn(4)} className="col-span-1 md:col-span-4 rounded-3xl bg-forest-900 noise-overlay text-bone p-8 border border-forest-900/10 flex flex-col" data-testid="feature-informes-programados">
          <div className="flex items-start gap-3 mb-5">
            <Mail className="w-8 h-8 text-terracotta shrink-0" />
            <span className="inline-flex items-center gap-1.5 rounded-full bg-terracotta/20 border border-terracotta/40 px-3 py-1 text-xs font-bold text-terracotta">NUEVO</span>
          </div>
          <h3 className="font-display font-bold text-2xl mb-3">Informes automáticos a tu gestoría</h3>
          <p className="font-body-landing text-bone/75 text-sm leading-relaxed mb-4">
            Programa el envío semanal o mensual del informe que necesites a los correos que quieras:
            RRHH, dirección, tu asesor laboral... Se generan y salen solos, en la fecha y hora que fijes.
          </p>
          <ul className="space-y-2.5 mt-auto font-body-landing text-sm text-bone/85">
            {['Semanal o mensual', 'PDF, Excel o ambos', 'Múltiples destinatarios', 'Auto-incluye a tus admins'].map((t) => (
              <li key={t} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-terracotta" />{t}</li>
            ))}
          </ul>
        </motion.div>

        {/* Cuadrantes y turnos */}
        <motion.div {...fadeIn(5)} className="col-span-1 md:col-span-6 rounded-3xl bg-forest-100 p-8 border border-forest-900/10 flex flex-col sm:flex-row gap-7 items-center" data-testid="feature-cuadrantes">
          <div className="flex-1">
            <div className="flex items-start gap-3 mb-5">
              <CalendarRange className="w-8 h-8 text-forest-700 shrink-0" />
              <span className="inline-flex items-center gap-1.5 rounded-full bg-forest-900/10 border border-forest-900/20 px-3 py-1 text-xs font-bold text-forest-900">NUEVO</span>
            </div>
            <h3 className="font-display font-bold text-2xl text-forest-900 mb-3">Cuadrantes y turnos rotativos</h3>
            <p className="font-body-landing text-forest-700 text-sm leading-relaxed">
              Planifica los turnos por empleado y día en una vista semanal.
              Copia la semana entera al siguiente periodo o aplica rotaciones circulares en un click.
              Ideal para hostelería, sanidad, retail o cualquier equipo con turnos alternos.
            </p>
          </div>
        </motion.div>

        {/* Autocierre inteligente */}
        <motion.div {...fadeIn(6)} className="col-span-1 md:col-span-6 rounded-3xl bg-white p-8 border border-forest-900/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col" data-testid="feature-autocierre">
          <div className="flex items-start gap-3 mb-5">
            <CheckCircle2 className="w-8 h-8 text-terracotta shrink-0" />
            <span className="inline-flex items-center gap-1.5 rounded-full bg-terracotta/10 border border-terracotta/25 px-3 py-1 text-xs font-bold text-terracotta">NUEVO</span>
          </div>
          <h3 className="font-display font-bold text-2xl text-forest-900 mb-3">Autocierre con validación del empleado</h3>
          <p className="font-body-landing text-forest-700 text-sm leading-relaxed">
            Si alguien se olvida de fichar la salida, el sistema cierra la jornada automáticamente según su horario
            configurado. Al día siguiente, el empleado <b>revisa y acepta</b> antes de que quede firme —
            cumpliendo el requisito de la nueva ley 2026: cada corrección debe validarla el trabajador.
          </p>
        </motion.div>
      </div>

      {/* Features secundarias */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
        {small.map((f, i) => (
          <motion.div key={f.t} {...fadeIn(i)} className="rounded-3xl bg-white border border-forest-900/10 p-7 hover:-translate-y-1 hover:shadow-[0_12px_36px_rgb(15,28,20,0.08)] transition-[transform,box-shadow] duration-300" data-testid={`feature-small-${i}`}>
            <f.icon className="w-6 h-6 text-terracotta mb-4" />
            <h4 className="font-display font-bold text-lg text-forest-900 mb-2">{f.t}</h4>
            <p className="font-body-landing text-sm text-forest-700 leading-relaxed">{f.d}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
